import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import {
  ValidateOtp,
  loginApi,
  resendPasswordOTP,
  sessionLogoutApi,
} from "../Utils/Axios";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../Context/AuthContext";
import "../LayOut/NewLogin/Message.css";
import Loader from "../Utils/Loader";
import { setAuthDetails } from "../Redux/AuthSlice";
import { useDispatch } from "react-redux";

const EmsLogin = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues, reset
  } = useForm({
    defaultValues: {
      username: "",
      password: "",
      otp: "",
    },
  });

  const { setAuthDetails: setContextAuthDetails } = useAuth();
  const navigate = useNavigate();
  const [passwordShown, setPasswordShown] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [otpTimeLimit, setOtpTimeLimit] = useState(180); // 3 minutes
  const [expiryTime, setExpiryTime] = useState(null);
  const [otpExpired, setOtpExpired] = useState(false);
  const [showSessionButton, setShowSessionButton] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!otpSent || !expiryTime) return;

    const timer = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.floor((expiryTime - Date.now()) / 1000)
      );
      setOtpTimeLimit(remaining);

      if (remaining <= 0) {
        clearInterval(timer);
        setOtpExpired(true);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [otpSent, expiryTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const sendOtp = (data) => {
    const payload = {
      username: data.username,
      password: data.password,
    };
    setLoading(true);
    loginApi(payload)
      .then((response) => {
        const token = response.data?.token;
        const refreshToken = response.data?.refreshToken;
        localStorage.setItem("refreshToken", refreshToken);
        if (token && refreshToken) {
          localStorage.setItem("token", token);
          const decodedToken = jwtDecode(token);
          const {
            sub: userId,
            roles: userRole,
            employee: employeeId,
            resourceType,
          } = decodedToken;
          dispatch(
            setAuthDetails({
              userId,
              userRole,
              employeeId,
              resourceType,
            })
          );
          setContextAuthDetails(token, refreshToken);
          toast.success("OTP Sent Successfully");
          setOtpSent(true);
          setOtpExpired(false);
          setOtpTimeLimit(180);
          setExpiryTime(Date.now() + 180 * 1000); // Set expiry to 3 minutes from now
        } else {
          setErrorMessage("Unexpected response format. Token not found.");
          setShowErrorModal(true);
        }
        setLoading(false);
      })
      .catch((error) => {
        handleLoginError(error, payload);
      });
  };

  const resendOtp = () => {
    const currentValues = getValues();
    setLoading(true);

    resendPasswordOTP({
      username: currentValues.username,
    })
      .then((response) => {
        setLoading(false);
        toast.success("New OTP Sent Successfully");
        setOtpExpired(false);
        setOtpTimeLimit(180);
        setExpiryTime(Date.now() + 180 * 1000); // Reset expiry to 3 minutes from now
      })
      .catch((error) => {
        setLoading(false);
        const errorMessage =
          error.response?.data?.message ||
          "Failed to resend OTP. Please try again.";
        setErrorMessage(errorMessage);
        setShowErrorModal(true);
      });
  };

  const verifyOtpAndEmsLogin = (data) => {
    const payload = {
      username: data.username,
      otp: data.otp,
      company: "company"
    };
    setLoading(true);
    ValidateOtp(payload)
      .then((response) => {
        setLoading(false);
        toast.success("Login Successful", {
          position: "top-right",
          transition: Bounce,
          hideProgressBar: true,
          theme: "colored",
          autoClose: 2000,
        });
        setTimeout(() => {
          navigate("/main");
        }, 1000);
      })
      .catch((error) => {
        setLoading(false);
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          setErrorMessage(error.response.data.error.message);
        } else {
          setErrorMessage("Login failed. Please try again later.");
        }
        setShowErrorModal(true);
      });
  };

  const handleSessionTimeout = async () => {
    if (!sessionData) return;
    setLoading(true);
    console.log("sessionData", sessionData);
    try {
      await sessionLogoutApi(sessionData); // pass the stored login data
      toast.success("Previous session logged out successfully.");
      setShowErrorModal(false);
      setShowSessionButton(false);
      setSessionData(null);
      navigate("/admin"); // redirect to login page
      reset(); // reset form fields
    } catch (err) {
      toast.error("Failed to logout previous session.");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginError = (error, loginData) => {
    let errorMessage = "Login failed. Please try again later.";
    let showSessionButton = false;

    if (error.response && error.response.data) {
      const data = error.response.data;
      if (data.isUserLoggedIn) {
        errorMessage = (
          <>
            {data.message || "User is already logged in."}
            <br />
            You need to log out from the old session to continue.
          </>
        );
        showSessionButton = true;
        setSessionData(loginData); // store login data to call sessionLogoutApi
      } else if (data.error && data.error.message) {
        errorMessage = data.error.message;
      }
    }

    setErrorMessage(errorMessage);
    setShowSessionButton(showSessionButton);
    setShowErrorModal(true);
  };

  const closeModal = () => {
    setShowErrorModal(false);
    setErrorMessage("");
    setLoading(false);
  };

  const togglePasswordVisibility = () => {
    setPasswordShown(!passwordShown);
  };

  const validatePassword = (value) => {
    const errors = [];
    if (!/(?=.*[0-9])/.test(value)) {
      errors.push("at least one digit");
    }
    if (!/(?=.*[a-z])/.test(value)) {
      errors.push("at least one lowercase letter");
    }
    if (!/(?=.*[A-Z])/.test(value)) {
      errors.push("at least one uppercase letter");
    }
    if (!/(?=.*[\W_])/.test(value)) {
      errors.push("at least one special character");
    }
    if (value.includes(" ")) {
      errors.push("no spaces");
    }

    if (errors.length > 0) {
      return `Password must contain ${errors.join(", ")}.`;
    }
    return true;
  };

  const onSubmit = (data) => {
    if (otpSent && !otpExpired) {
      verifyOtpAndEmsLogin(data);
    } else {
      sendOtp(data);
    }
  };
  const preventSpaces = (e) => {
    if (e.key === " ") {
      e.preventDefault();
    }
  };

  return (
    <div>
      <main className="newLoginMainWrapper">
        {loading && <Loader />}
        <div className="newLoginWrapper">
          <div className="newLoginContainer">
            <div className="newLoginLeftSectionOuter">
              <div className="newLoginLeftTitle">
                Welcome To <br /> Employee Management System
              </div>
              <div className="newLoginLeftImgHolder">
                <img src="..\assets\img\left-img.png" alt="#" />
              </div>
            </div>
            <div className="newLoginRightSectionOuter">
              <div className="newLoginRightSection">
                {/* <div>
                  <img src="/assets/img/pathbreaker_logo.png" alt="logo" />
                </div> */}
                <div className="newLoginRightSecTitle">Login</div>
                <div className="newLoginRightSecSelectLogin">
                  <div className="loginBtn">
                    <span>Continue With EMS Login</span>
                  </div>
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="formgroup">
                    <label className="form-label">Email Id</label>
                    <input
                      className="form-control form-control-lg"
                      type="email"
                      placeholder="Email Id"
                      autoComplete="off"
                      readOnly={otpSent}
                      onKeyDown={preventSpaces}
                      {...register("username", {
                        required: "Email Id is Required.",
                        pattern: {
                          value:
                            /^[a-z][a-zA-Z0-9._+-]*@[a-zA-Z0-9.-]+\.(com|in|org|net|edu|gov)$/,
                          message: "Invalid Email Id Format",
                        },
                      })}
                    />
                    {errors.username && (
                      <p className="errorMsg">{errors.username.message}</p>
                    )}
                  </div>

                  {!otpSent ? (
                    <div className="formgroup">
                      <label className="form-label">Password</label>
                      <div className="password-input-container">
                        <input
                          className="form-control form-control-lg"
                          placeholder="Password"
                          autoComplete="off"
                          type={passwordShown ? "text" : "password"}
                          maxLength={16}
                          onKeyDown={preventSpaces}
                          {...register("password", {
                            required: "Password is Required",
                            minLength: {
                              value: 6,
                              message:
                                "Password must be at least 6 characters long",
                            },
                            validate: validatePassword,
                          })}
                        />
                        <span
                          className={`bi bi-eye field-icon pb-1 toggle-password ${passwordShown ? "text-primary" : ""
                            }`}
                          onClick={togglePasswordVisibility}
                        ></span>
                      </div>
                      {errors.password && (
                        <p className="errorMsg">{errors.password.message}</p>
                      )}
                      <small>
                        <a href="/forgotPassword">Forgot Password?</a>
                      </small>
                    </div>
                  ) : (
                    <div className="formgroup">
                      <label className="form-label">OTP</label>
                      <input
                        className="form-control form-control-lg"
                        placeholder="Enter Your OTP"
                        autoComplete="off"
                        disabled={otpExpired}
                        onKeyDown={preventSpaces}
                        maxLength={6}
                        inputMode="numeric"
                        {...register("otp", {
                          required: "OTP is Required.",
                          pattern: {
                            value: /^\d{6}$/,
                            message: "OTP must be 6 digits.",
                          },
                        })}
                      />
                      {errors.otp && (
                        <p className="errorMsg">{errors.otp.message}</p>
                      )}
                      <div className="otp-timer">
                        {!otpExpired ? (
                          <span className="text-primary">
                            OTP expires in: {formatTime(otpTimeLimit)}
                          </span>
                        ) : (
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-danger">OTP expired</span>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-primary"
                              onClick={resendOtp}
                            >
                              Resend OTP
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="d-grid gap-2 mt-3">
                    <button className="btn btn-lg btn-primary" type="submit">
                      {otpSent ? "Verify OTP" : "Sign in"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Modal
        show={showErrorModal}
        onHide={closeModal}
        centered
        style={{ zIndex: "1050" }}
        className="custom-modal"
      >
        <ModalHeader>
          <ModalTitle className="text-center">Error</ModalTitle>
          <button
            type="button"
            className="text-dark"
            aria-label="Close"
            onClick={closeModal}
          >
            X
          </button>
        </ModalHeader>
        <ModalBody className="text-center fs-bold">{errorMessage}</ModalBody>
        <ModalFooter>
          {showSessionButton && (
            <div className="mt-3">
              <button
                className="btn btn-secondary me-2"
                onClick={() => closeModal()}
              >
                No
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleSessionTimeout()}
              >
                Yes! Logout Previous Session
              </button>
            </div>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default EmsLogin;
